import { SessionEventType, type SessionEvent } from "@mycompbox/shared";
import type { JobAttachment, JobTurn } from "@/lib/api/mailbox";

/**
 * One idea's stream (docs/new-look/PLAN.md, step 4.2): the conversation with
 * each helper and the build's activity, merged in time order.
 */

export type StreamWho = "you" | "ideaHelper" | "blueprintMaker" | "builder" | "compbox";

export interface StreamItem {
  id: string;
  /** ISO time. */
  at: string;
  /** "talk": a message between the person and a helper; "activity": something that happened. */
  kind: "talk" | "activity";
  who: StreamWho;
  text: string;
  tone: "plain" | "ok" | "error";
  /** Files sent with a message (ER-133). */
  attachments?: readonly JobAttachment[];
}

export interface StreamSources {
  /** The interview's turns (Idea helper). */
  interviewTurns?: readonly JobTurn[] | undefined;
  /** The planning run's turns (Blueprint maker). */
  planTurns?: readonly JobTurn[] | undefined;
  /** The build run's turns (Builder). */
  buildTurns?: readonly JobTurn[] | undefined;
  /** The blueprint's session events (parts and tasks started and finished). */
  events?: readonly SessionEvent[] | undefined;
}

function talk(
  turns: readonly JobTurn[] | undefined,
  helper: StreamWho,
  source: string
): StreamItem[] {
  return (turns ?? [])
    .filter((t) => t.content.trim().length > 0 || (t.attachments?.length ?? 0) > 0)
    .map((t) => ({
      id: `${source}:${t.id}`,
      at: t.createdAt,
      kind: "talk",
      who: t.role === "user" ? "you" : helper,
      text: t.content.trim(),
      tone: "plain",
      ...(t.attachments && t.attachments.length > 0 && { attachments: t.attachments }),
    }));
}

function named(payload: Record<string, unknown> | undefined): string {
  const identifier = typeof payload?.identifier === "string" ? payload.identifier : "";
  const title = typeof payload?.title === "string" ? payload.title : "";
  return [identifier, title].filter(Boolean).join(": ");
}

/** The words for one session event, or null for events that aren't worth a line. */
export function eventLine(event: SessionEvent): { text: string; tone: StreamItem["tone"] } | null {
  const p = event.payload as Record<string, unknown> | undefined;
  const num = (key: string) => (typeof p?.[key] === "number" ? String(p[key]) : "?");
  switch (event.eventType) {
    case SessionEventType.SESSION_STARTED:
      return { text: "Build session started", tone: "plain" };
    case SessionEventType.SESSION_ENDED:
      return { text: "Build session ended", tone: "plain" };
    case SessionEventType.SESSION_PHASE_STARTED:
      return {
        text: `Phase ${num("phaseNumber")} of ${num("totalPhases")} started`,
        tone: "plain",
      };
    case SessionEventType.SESSION_PHASE_COMPLETED:
      return { text: `Phase ${num("phaseNumber")} of ${num("totalPhases")} done`, tone: "ok" };
    case SessionEventType.SESSION_FEATURE_STARTED:
      return { text: `Started part ${named(p)}`, tone: "plain" };
    case SessionEventType.SESSION_FEATURE_COMPLETED:
      return { text: `Finished part ${named(p)}`, tone: "ok" };
    case SessionEventType.SESSION_TASK_STARTED:
      return { text: `Working on ${named(p)}`, tone: "plain" };
    case SessionEventType.SESSION_TASK_COMPLETED:
      return { text: `Done: ${named(p)}`, tone: "ok" };
    case SessionEventType.SESSION_TASK_PROGRESS:
      return typeof p?.message === "string" && p.message.trim()
        ? { text: p.message.trim(), tone: "plain" }
        : null;
    case SessionEventType.SESSION_ERROR:
      return {
        text: typeof p?.message === "string" ? p.message : "Something went wrong",
        tone: "error",
      };
    default:
      return null;
  }
}

/** Every message and event, oldest first; ties keep talk before activity. */
export function buildStream(sources: StreamSources): StreamItem[] {
  const activity: StreamItem[] = [];
  for (const [i, event] of (sources.events ?? []).entries()) {
    const line = eventLine(event);
    if (!line) continue;
    activity.push({
      id: `event:${String(i)}:${event.timestamp}`,
      at: event.timestamp,
      kind: "activity",
      who: "builder",
      ...line,
    });
  }
  const items = [
    ...talk(sources.interviewTurns, "ideaHelper", "interview"),
    ...talk(sources.planTurns, "blueprintMaker", "plan"),
    ...talk(sources.buildTurns, "builder", "build"),
    ...activity,
  ];
  return items
    .map((item, order) => ({ item, order, time: new Date(item.at).getTime() }))
    .sort((a, b) => a.time - b.time || a.order - b.order)
    .map(({ item }) => item);
}
